import type { Zone } from '@seo/contracts';

export interface NavItem {
  key: string;
  icon: string;
  label: string;
  to: string;
  zone: Zone;
  /**
   * Which counter from `session.navBadges` this row shows, if any.
   *
   * The field used to be a plain `badge?: string` that nothing ever set, so the sidebar
   * carried no numbers at all while the mockup puts one beside ten of its fifteen rows.
   * Naming the source rather than the value is what keeps them true: a hardcoded «5.2k»
   * is a screenshot, not a badge.
   */
  badge?: keyof NavBadgeCounts;
}

/** Mirrors `NavBadgeCounts` in `@seo/core`; `null` means "not readable by this actor". */
export interface NavBadgeCounts {
  sites: number | null;
  brands: number | null;
  games: number | null;
  clusters: number | null;
  geo: number | null;
  templates: number | null;
  guestPosts: number | null;
  openAbuseCases: number | null;
}

export interface NavGroup {
  title: string;
  items: NavItem[];
}

/**
 * Navigation, structure and glyphs taken from the mockup's `NAV` constant.
 *
 * Each row declares the zone it belongs to; the shell hides rows the permission matrix
 * would refuse. Hiding is cosmetic — the API refuses them regardless (§7).
 *
 * **This list equals the mockup's `NAV` exactly: 6 groups, 15 items, same order, same
 * labels.** It used to carry five more rows and a seventh group, all of them screens the
 * mockup reaches from an entity instead of the sidebar:
 *
 * - `Ядро (ключі)` → the «Ключові слова» tab of a brand / game / cluster, plus `kwImport`;
 * - `Візард збірки`, `Редактор сторінок` → site-scoped, entered from the site card;
 * - `Масові операції` → entered from the «МАСОВІ ДІЇ» bar of the sites registry;
 * - `Журнал змін` → the «Журнал змін» tab of the site card.
 *
 * The routes stay; only the sidebar rows are gone. Deleting the routes with them would
 * point four sidebar entries at 404s and lose four working screens.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Огляд',
    items: [
      { key: 'dash', icon: '◧', label: 'Дашборд', to: '/', zone: 'dashboards' },
      {
        key: 'monitor',
        icon: '◉',
        label: 'Моніторинг & абуз',
        to: '/monitoring',
        zone: 'monitoring',
        badge: 'openAbuseCases',
      },
    ],
  },
  {
    title: 'Сайти',
    items: [
      { key: 'sites', icon: '▤', label: 'Сайти', to: '/sites', zone: 'site_seo', badge: 'sites' },
      { key: 'emd', icon: '⌗', label: 'EMD генератор', to: '/emd', zone: 'emd' },
    ],
  },
  {
    title: 'Бази даних',
    items: [
      { key: 'brands', icon: '◈', label: 'Бренди', to: '/brands', zone: 'databases', badge: 'brands' },
      { key: 'slots', icon: '◇', label: 'Слоти та ігри', to: '/games', zone: 'databases', badge: 'games' },
      {
        key: 'clusters',
        icon: '⋮⋮',
        label: 'Кластери',
        to: '/clusters',
        zone: 'databases',
        badge: 'clusters',
      },
      {
        key: 'geo',
        icon: '⊙',
        label: 'ГЕО та мови',
        to: '/geo',
        zone: 'geo_dictionaries',
        badge: 'geo',
      },
      { key: 'refs', icon: '⊞', label: 'Довідники', to: '/reference', zone: 'databases' },
    ],
  },
  {
    title: 'Контент',
    items: [
      // The generation-queue counter the mockup shows here waits for the `jobs` read API
      // in Stage 3. A number nobody can compute is not a badge, it is a decoration.
      { key: 'content', icon: '✎', label: 'Контент сайтів', to: '/prompts', zone: 'ai_generation' },
      {
        key: 'blocks',
        icon: '▦',
        label: 'Конструктор шаблонів',
        to: '/templates',
        zone: 'ai_generation',
        badge: 'templates',
      },
    ],
  },
  {
    title: 'Лінки',
    items: [
      { key: 'links', icon: '⇥', label: 'Лінкбілдинг', to: '/links', zone: 'linkbuilding' },
      {
        key: 'cgen',
        icon: '¶',
        label: 'Контент для лінків',
        to: '/content-generator',
        zone: 'linkbuilding',
        badge: 'guestPosts',
      },
    ],
  },
  {
    title: 'Система',
    items: [
      { key: 'settings', icon: '⚙', label: 'Налаштування', to: '/settings', zone: 'system_admin' },
      { key: 'users', icon: '☰', label: 'Юзери & доступи', to: '/users', zone: 'system_admin' },
    ],
  },
];

/** Screen titles and breadcrumbs, matching the mockup's `SCREENS` map. */
export const SCREEN_META: Record<string, { title: string; crumb: string }> = {
  '/': { title: 'Загальний дашборд', crumb: 'огляд · всі проєкти · 28 днів' },
  '/sites': { title: 'Сайти', crumb: 'реєстр · фільтри та масові дії' },
  '/sites/new': { title: 'Новий сайт', crumb: 'сайти / створення · 6 кроків' },
  '/emd': { title: 'EMD генератор', crumb: 'сайти / генератор доменів' },
  '/brands': { title: 'База брендів', crumb: 'бази / бренди казино' },
  '/games': { title: 'База слотів', crumb: 'бази / ігри та слоти' },
  '/clusters': { title: 'Ревʼю-кластери', crumb: 'бази / кластери' },
  '/geo': { title: 'ГЕО та мовні пресети', crumb: 'бази / гео' },
  '/reference': { title: 'Довідники', crumb: 'бази / платіжки, провайдери, ліцензії, RG' },
  '/core': { title: 'Ядро', crumb: 'бази / ключі · імпорт CSV/XLSX і Search Console' },
  '/prompts': { title: 'Контент сайтів', crumb: 'контент / промпти, шаблони, черга генерації' },
  '/templates': { title: 'Конструктор шаблонів', crumb: 'контент / шаблони / блоки та порядок' },
  '/build': { title: 'Візард збірки', crumb: 'контент / збірка сайту · 6 кроків' },
  '/editor': { title: 'Редактор сторінок', crumb: 'контент / блоки, версії, публікація' },
  '/links': { title: 'Лінкбілдинг', crumb: 'лінки / план, анкори, гест-пости' },
  '/content-generator': { title: 'Контент для лінків', crumb: 'лінки / гестпости: теми, тексти, аудит' },
  '/monitoring': { title: 'Моніторинг та абузи', crumb: 'огляд / індекс, сервери, скарги' },
  '/bulk': { title: 'Масова переклейка', crumb: 'сайти / масові дії / canonical + hreflang' },
  '/audit': { title: 'Журнал змін', crumb: 'система / аудит' },
  '/settings': { title: 'Глобальні налаштування', crumb: 'система / усе, що впливає на багато процесів' },
  '/users': { title: 'Юзери та доступи', crumb: 'система / роль-модель' },
};
